import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Crown, Star, User } from 'lucide-react';
import WidgetCard from './WidgetCard';
import { useAuthStore } from '../../../store/useAuthStore';
import { getSubscriptionDetails, getPaymentHistory } from '../../../services/billingService';
import { SubscriptionDetails, PaymentRecord } from '../../../types/billing';

const tierConfig = {
  free: {
    label: 'Free',
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    icon: <User className="w-4 h-4" />,
  },
  pro: {
    label: 'Professional',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    icon: <Star className="w-4 h-4" />,
  },
  enterprise: {
    label: 'Enterprise',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
    icon: <Crown className="w-4 h-4" />,
  },
};

export default function AccountBillingWidget() {
  const { user } = useAuthStore();
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      setLoading(true);
      const [subRes, payRes] = await Promise.all([
        getSubscriptionDetails(user.id),
        getPaymentHistory(user.id, 3),
      ]);
      if (subRes.data) setSubscription(subRes.data);
      if (payRes.data) setPayments(payRes.data);
      setLoading(false);
    };

    fetchData();
  }, [user]);

  const tier = subscription?.tier || 'free';
  const config = tierConfig[tier] || tierConfig.free;

  return (
    <WidgetCard
      title="Account & Billing"
      icon={<CreditCard className="w-5 h-5" />}
      isLoading={loading}
    >
      <div className="space-y-4">
        {/* Subscription tier badge */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`${config.bgColor} ${config.color} p-1.5 rounded-lg`}>
              {config.icon}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{config.label}</p>
              <p className="text-xs text-gray-500">Current Plan</p>
            </div>
          </div>
          {tier === 'free' && (
            <Link
              to="/pricing"
              className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Upgrade
            </Link>
          )}
        </div>

        {/* Subscription renewal date */}
        {subscription?.endsAt && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-2">
            Renews {new Date(subscription.endsAt).toLocaleDateString()}
            {subscription.paymentMethodLast4 && (
              <span> &middot; Card ending {subscription.paymentMethodLast4}</span>
            )}
          </div>
        )}

        {/* Recent payments */}
        {payments.length > 0 && (
          <div>
            <p className="text-xs text-gray-500 mb-2">Recent Payments</p>
            <div className="space-y-1.5">
              {payments.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between text-xs"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-1.5 h-1.5 rounded-full ${
                        p.status === 'completed'
                          ? 'bg-green-500'
                          : p.status === 'pending'
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                      }`}
                    />
                    <span className="text-gray-600 truncate max-w-[120px]">
                      {p.description || 'Payment'}
                    </span>
                  </div>
                  <span className="font-medium text-gray-900">
                    ${p.amount.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {payments.length === 0 && tier === 'free' && (
          <p className="text-xs text-gray-400">
            No payment history. Purchase a report or subscribe to get started.
          </p>
        )}

        {/* Manage link */}
        <Link
          to="/user-settings"
          className="block text-center text-xs text-blue-600 hover:text-blue-800 font-medium"
        >
          Manage Account Settings
        </Link>
      </div>
    </WidgetCard>
  );
}
